<script setup lang="ts">
import { Attribute } from "@/data/entities";
import { computed, reactive, watch } from "vue";
import {
  createOptionMatrix,
  createValueMatrix,
  pivotMatrix,
  isTableData,
  stringToTableData,
} from "./functions";
import { dialogEvents } from "@/data-views/dialogs";
import { toast } from "vue3-toastify";
import { InputAttribute, TableValue, Dimension, TableCell } from "./types";
const OPTION_ID_SEPARATOR = "---";

/////////////////////////////////////////////////////////////
//////////////////////// PROPS //////////////////////////////
/////////////////////////////////////////////////////////////
const props = defineProps<{
  attributes: InputAttribute[];
  formatter: (value: string | number) => string;
  modelValue: TableValue[];
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: TableValue[]): void;
}>();

/////////////////////////////////////////////////////////////
//////////////////////// SETUP //////////////////////////////
/////////////////////////////////////////////////////////////
const { t } = useI18n();

/////////////////////////////////////////////////////////////
//////////////////////// STATE //////////////////////////////
/////////////////////////////////////////////////////////////
const xAxis = reactive({
  dimensions: [] as Dimension[],
  optionMatrix: [] as TableCell[][],
});

const yAxis = reactive({
  dimensions: [] as Dimension[],
  optionMatrix: [] as TableCell[][],
});

const state = reactive({
  loading: false,
  copied: false,
  matrix: [] as TableValue[][],
  xAxisAttributes: [] as string[],
  yAxisAttributes: [] as string[],
});

/////////////////////////////////////////////////////////////
////////////////////// COMPUTED /////////////////////////////
/////////////////////////////////////////////////////////////
const value = computed({
  get: () => props.modelValue,
  set: (value: TableValue[]) => emit("update:modelValue", value),
});

/////////////////////////////////////////////////////////////
////////////////////// METHODS //////////////////////////////
/////////////////////////////////////////////////////////////
function load(): void {
  if (props.attributes.length > 0) {
    console.log(props.attributes);
    buildDimensions();
    console.log(xAxis.dimensions);
    buildOptionMatrices();
    buildMatrix();
  }
}

function buildDimensions(): void {
  // Select the first attribute as the x axis dimension
  if (state.xAxisAttributes.length === 0) {
    state.xAxisAttributes = [props.attributes[0].id];
  }

  xAxis.dimensions = props.attributes
    .filter((attribute: InputAttribute) =>
      state.xAxisAttributes.includes(attribute.id),
    )
    .map((attribute: InputAttribute) => {
      return {
        id: attribute.id,
        name: attribute.label,
        values: attribute.values,
      };
    })
    .sort((a: Dimension, b: Dimension) => {
      return a.values.length - b.values.length;
    });

  // Select the remaining attributes as y-axis dimensions and sort by amount of values, descending
  yAxis.dimensions = props.attributes
    .filter(
      (attribute: InputAttribute) =>
        !state.xAxisAttributes.includes(attribute.id),
    )
    .map((attribute: InputAttribute) => {
      return {
        id: attribute.id,
        name: attribute.label,
        values: attribute.values,
      };
    })
    .sort((a: Dimension, b: Dimension) => {
      return a.values.length - b.values.length;
    });
}

function buildOptionMatrices(): void {
  // Create the option matrix for the x-axis
  xAxis.optionMatrix = createOptionMatrix(xAxis.dimensions, "x");

  // Create the option matrix for the y-axis
  yAxis.optionMatrix = createOptionMatrix(yAxis.dimensions, "y");
}

function buildMatrix(): void {
  // Create the value matrix
  state.matrix = createValueMatrix(xAxis, yAxis);

  // Fill the value matrix with values
  for (let i = 0; i < state.matrix.length; i++) {
    for (let j = 0; j < state.matrix[i].length; j++) {
      const matchingValue = props.modelValue?.find((value: TableValue) => {
        return Object.entries(value.attributes).every(([key, value]) => {
          return (
            state.matrix[i][j].attributes[key]?.toString() === value?.toString()
          );
        });
      });

      if (matchingValue) {
        state.matrix[i][j].value = {
          textValue: props.formatter(matchingValue.value.floatValue),
          floatValue: matchingValue.value.floatValue,
        };
      } else {
        state.matrix[i][j].value = {
          textValue: props.formatter(0),
          floatValue: 0,
        };
      }
    }
  }
}

/**
 * Handles on blur event
 */
function onBlur(i: number, j: number): void {
  // Update value
  let textValue = state.matrix[i][j].value.textValue
    .replace(".", "")
    .replace(",", ".");
  textValue = textValue.replace(/[^0-9.]/g, "");

  state.matrix[i][j].value.floatValue = parseFloat(textValue);
  state.matrix[i][j].value.textValue = props.formatter(
    state.matrix[i][j].value.floatValue,
  );

  // Remove focus effect
  removeFocusEffect();
}

/**
 * Removes focus effect
 */
function removeFocusEffect(): void {
  // Remove all active
  document
    .querySelectorAll(
      "th.active, th.active-row, th.active-col, td.active, td.active-row, td.active-col",
    )
    .forEach((element) => {
      element.classList.remove("active");
      element.classList.remove("active-row");
      element.classList.remove("active-col");
    });
}

/**
 * Adds focus effect to specified row and column in the table.
 * @param {number} i - The row index.
 * @param {number} j - The column index.
 * @returns {void}
 */
function addFocusEffect(i: number, j: number): void {
  // Remove all active
  removeFocusEffect();

  // Add active class to value cell
  document
    .querySelector(`.value-cell[data-row-index="${i}"][data-col-index="${j}"]`)
    ?.classList.add("active");

  // Add active class to the row
  document
    .querySelectorAll(`.value-cell[data-row-index="${i}"]`)
    .forEach((element) => {
      element.classList.add("active-row");
    });

  // Add active class to the column
  document
    .querySelectorAll(`.value-cell[data-col-index="${j}"]`)
    .forEach((element) => {
      element.classList.add("active-col");
    });

  // Add active class to the row and column headers
  const attributes = state.matrix[i][j].attributes;
  const xAxisAttributes = Object.keys(attributes).filter((key) =>
    xAxis.dimensions.some((dimension) => dimension.id === key),
  );
  const yAxisAttributes = Object.keys(attributes).filter((key) =>
    yAxis.dimensions.some((dimension) => dimension.id === key),
  );
  const xAxisOptions = xAxisAttributes.map((key) => attributes[key]);
  const yAxisOptions = yAxisAttributes.map((key) => attributes[key]);

  let selector = "";
  xAxisOptions.forEach((id) => {
    selector += id;
    document
      .querySelectorAll(`.x-axis-header[data-options="${selector}"]`)
      .forEach((element) => {
        element.classList.add("active");
      });
    selector += OPTION_ID_SEPARATOR;
  });

  selector = "";
  yAxisOptions.forEach((id) => {
    selector += id;
    document
      .querySelectorAll(`.y-axis-header[data-options="${selector}"]`)
      .forEach((element) => {
        element.classList.add("active");
      });
    selector += OPTION_ID_SEPARATOR;
  });
}

/****
 * Sets focus to the input element located at the specified row and column.
 *
 * @param {number} row - The row where the input element is located.
 * @param {number} column - The column where the input element is located.
 * @returns {void}
 */
async function setFocusToInput(row: number, column: number): Promise<void> {
  nextTick(() => {
    const element = document.querySelector(
      '.value-cell[data-row-index="' +
        row +
        '"][data-col-index="' +
        column +
        '"] input',
    );
    if (element) {
      (element as any).focus();
    }
  });
}

/****
 * Handles the paste event, processes the pasted content to extract table data, and updates the value matrix.
 * @param {any} event - The paste event object containing the clipboard data.
 * @returns {void}
 */
async function handlePaste(event: any): Promise<void> {
  // Get clipboard
  const clipboardData = event.clipboardData || window.Clipboard;
  const pastedContent = clipboardData.getData("text/plain");

  const [isTable, content] = isTableData(pastedContent);
  if (isTable) {
    // Get table data
    const tableData = stringToTableData(content);

    // Empty input
    event.preventDefault();
    const pastedInput = event.target as HTMLInputElement;
    pastedInput.value = "";

    // Input coordinates
    const inputRowIndex = parseInt(
        pastedInput.parentElement?.getAttribute("data-row-index") ?? "0",
      ),
      inputColumnIndex = parseInt(
        pastedInput.parentElement?.getAttribute("data-col-index") ?? "0",
      );

    // Ask user if content needs to be pivoted
    if (state.matrix.length < tableData.length) {
      dialogEvents.ConfirmDialog.open(
        t("confirmation.pivot-table.title"),
        async () => {
          const pivotedTableData = pivotMatrix(tableData);
          pasteMatrix(pivotedTableData, inputRowIndex, inputColumnIndex);
        },
        async () => {
          pasteMatrix(tableData, inputRowIndex, inputColumnIndex);
        },
      );
    } else {
      pasteMatrix(tableData, inputRowIndex, inputColumnIndex);
    }
  }
}

/**
 * Function to paste a matrix of data into the input table.
 *
 * @param {Array<Array<string | number>>} tableData - The matrix of data to be pasted.
 * @param {number} [offsetRow=0] - The offset for the row index.
 * @param {number} [offsetCol=0] - The offset for the column index.
 * @returns {void}
 */
function pasteMatrix(
  tableData: (string | number)[][],
  offsetRow = 0,
  offsetCol = 0,
): void {
  // Output the table data as an array of arrays of floats
  for (const rowIndex in tableData) {
    for (const colIndex in tableData[rowIndex]) {
      const i = parseInt(rowIndex) + offsetRow;
      const j = parseInt(colIndex) + offsetCol;

      // Set value
      if (state.matrix[i] && state.matrix[i][j]) {
        state.matrix[i][j].value.textValue =
          tableData[rowIndex][colIndex].toString();
        state.matrix[i][j].value.floatValue = parseFloat(
          tableData[rowIndex][colIndex].toString(),
        );
      }
    }
  }
}

/****
 * Copies the data from a matrix to the clipboard in a tabular format.
 * @returns {void}
 */
function handleCopy() {
  // Get size
  const numberOfRows = state.matrix.length,
    numberOfColumns = state.matrix[0].length;

  // Convert to table string
  let tableString = "";
  for (let i = 0; i < numberOfRows; i++) {
    for (let j = 0; j < numberOfColumns; j++) {
      tableString +=
        state.matrix[i][j].value.textValue.replace(",", ".") + "\t";
    }

    tableString += "\n";
  }

  // Copy to clipboard
  navigator.clipboard.writeText(tableString);

  // Toast
  toast.success(t("table.copied"));

  state.copied = true;
  setTimeout(() => {
    state.copied = false;
  }, 3000);
}

/****
 * Flips the table either vertically or horizontally.
 * @param {string} [direction='vertical'] - The direction to flip the table, either 'vertical' or 'horizontal'.
 * @returns {void}
 */
function handleFlip(direction: "vertical" | "horizontal" = "vertical") {
  // Get size
  const numberOfRows = state.matrix.length,
    numberOfColumns = state.matrix[0].length;

  // Flip table
  const matrix = JSON.parse(JSON.stringify(state.matrix));
  if (direction === "vertical") {
    for (let i = 0; i < numberOfRows; i++) {
      state.matrix[i] = matrix[numberOfRows - 1 - i];
      state.matrix[numberOfRows - 1 - i] = matrix[i];
    }
  } else {
    for (let i = 0; i < numberOfRows; i++) {
      for (let j = 0; j < numberOfColumns; j++) {
        state.matrix[i][j] = matrix[i][numberOfColumns - 1 - j];
        state.matrix[i][numberOfColumns - 1 - j] = matrix[i][j];
      }
    }
  }
}

/////////////////////////////////////////////////////////////
////////////////////// WATCHERS /////////////////////////////
/////////////////////////////////////////////////////////////

watch(
  () => state.matrix,
  (newValue) => {
    value.value = newValue.flat();
  },
  { deep: true },
);

/////////////////////////////////////////////////////////////
////////////////////// LIFECYCLES ///////////////////////////
/////////////////////////////////////////////////////////////

onMounted(() => {
  load();
});
</script>

<template>
  <VRow v-if="state.matrix.length > 0 && state.matrix[0].length > 0">
    <VCol cols="12" class="d-flex justify-end">
      <VBtn
        v-if="state.matrix.length > 0 && state.matrix[0].length > 1"
        size="small"
        class="ml-2"
        icon
        color="primary"
        :disabled="state.copied || props.disabled"
        :loading="state.loading"
        @click="handleFlip('horizontal')"
      >
        <VIcon icon="tabler-arrows-right-left" />
      </VBtn>
      <VBtn
        v-if="state.matrix.length > 1"
        size="small"
        class="ml-2"
        icon
        color="primary"
        :disabled="state.copied || props.disabled"
        :loading="state.loading"
        @click="handleFlip('vertical')"
      >
        <VIcon icon="tabler-arrows-up-down" />
      </VBtn>
      <VBtn
        size="small"
        class="ml-2"
        icon
        color="primary"
        :disabled="state.copied"
        :loading="state.loading"
        @click="handleCopy"
      >
        <VIcon icon="tabler-copy" />
      </VBtn>
    </VCol>
    <VCol cols="12" class="input-table__outer">
      <VTable
        class="input-table"
        fixed-header
        density="compact"
        style="max-height: 50vh"
      >
        <thead>
          <tr>
            <th
              v-if="yAxis.optionMatrix[0].length > 0"
              class="empty-corner"
              :rowspan="xAxis.optionMatrix.length"
              :colspan="yAxis.optionMatrix[0].length"
            ></th>
            <th
              class="x-axis-header-label"
              :colspan="xAxis.optionMatrix[0].length"
            >
              {{
                xAxis.dimensions
                  .map((dimension) => $t(dimension.name))
                  .join(" x ")
              }}
            </th>
          </tr>
          <tr v-for="(optionCells, rowIndex) in xAxis.optionMatrix">
            <th
              v-if="
                rowIndex === xAxis.optionMatrix.length - 1 &&
                yAxis.optionMatrix[0].length > 0
              "
              class="y-axis-header-label"
              :colspan="yAxis.optionMatrix[0].length"
            >
              {{
                yAxis.dimensions
                  .map((dimension) => $t(dimension.name))
                  .join(" x ")
              }}
            </th>
            <td
              v-if="optionCells.length > 1 || yAxis.optionMatrix[0].length > 0"
              v-for="(cell, colIndex) in optionCells.filter(
                (cell) => cell.show,
              )"
              class="x-axis-header"
              :rowspan="cell.nRows"
              :colspan="cell.nCols"
              :data-row-index="rowIndex"
              :data-col-index="colIndex"
              :data-options="cell.ids.join(OPTION_ID_SEPARATOR)"
            >
              {{ $t(cell.label) }}
            </td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(optionCells, rowIndex) in yAxis.optionMatrix">
            <td
              v-for="(cell, colIndex) in optionCells.filter(
                (cell) => cell.show,
              )"
              class="y-axis-header"
              :rowspan="cell.nRows"
              :colspan="cell.nCols"
              :data-row-index="rowIndex"
              :data-col-index="colIndex"
              :data-options="cell.ids.join(OPTION_ID_SEPARATOR)"
            >
              {{ $t(cell.label) }}
            </td>
            <td
              v-for="(valueCell, colIndex) in state.matrix[rowIndex]"
              class="value-cell"
              :data-row-index="rowIndex"
              :data-col-index="colIndex"
            >
              <input
                type="text"
                :disabled="props.disabled"
                v-model="valueCell.value.textValue"
                @keydown.enter="setFocusToInput(rowIndex + 1, colIndex)"
                @focus="addFocusEffect(rowIndex, colIndex)"
                @blur="onBlur(rowIndex, colIndex)"
                @paste="handlePaste"
              />
            </td>
          </tr>
        </tbody>
      </VTable>
    </VCol>
    <VCol v-if="props.attributes.length > 1" cols="12" md="4">
      <AppSelect
        label="X-axis"
        v-model="state.xAxisAttributes"
        :disabled="disabled"
        multiple
        :items="
          props.attributes.map((attribute) => ({
            title: attribute.label,
            value: attribute.id,
          }))
        "
        @update:modelValue="load"
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index < 1">
            <span>{{ item.title }}</span>
          </v-chip>
          <span
            v-if="index === 1"
            class="text-grey text-caption align-self-center"
          >
            (+{{ parseInt(state.xAxisAttributes.length.toString()) - 1 }}
            {{ $t("others") }})
          </span>
        </template>
      </AppSelect>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@/assets/styles/styles.scss";

.input-table {
  width: 100%;

  th,
  td {
    padding: 0 !important;
    margin: 0 !important;
    padding-inline: 0;
    padding-inline-start: 0 !important;
    padding-inline-end: 0 !important;
    text-align: left;
    overflow: hidden;
    white-space: nowrap; // Allows text to wrap to the next line
    text-overflow: ellipsis; // Adds ellipsis if the text is too long
    border: 1px solid rgba(var(--v-theme-primary), 0.1);

    &:nth-child(1) {
      text-align: center;
    }

    &.active {
      background-color: rgba(var(--v-theme-primary), 0.3) !important;

      input {
        background-color: rgba(var(--v-theme-primary), 0.3) !important;
      }
    }

    &.active-row,
    &.active-col {
      background-color: rgba(var(--v-theme-primary), 0.1);
    }
  }

  td:first-child {
    padding-inline-start: 0px !important;
  }

  td:last-child {
    padding-inline-end: 0px !important;
  }

  .x-axis-header,
  .y-axis-header {
    text-align: center !important;
    padding: 12px !important;
    background-color: rgba(var(--v-theme-background));
    color: rgba(var(--v-theme-on-background));

    &.active {
      background-color: rgba(var(--v-theme-primary)) !important;
      color: rgba(var(--v-theme-on-primary)) !important;
    }
  }

  .empty-corner,
  .x-axis-header-label,
  .y-axis-header-label {
    text-align: center !important;
    padding: 8px;
    background-color: rgba(var(--v-theme-background)) !important;
    color: rgba(var(--v-theme-on-background)) !important;

    &.active {
      background-color: rgba(var(--v-theme-primary)) !important;
      color: rgba(var(--v-theme-on-primary)) !important;
    }
  }

  .value-cell {
    min-width: 80px !important;

    input {
      width: 100%;
      height: 100%;
      text-align: right;
      padding: 0 0.5em;
    }
  }
}
</style>
